import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";

/* hs-eslint ignored failing-rules */

/* eslint-disable react-hooks/exhaustive-deps */
import FormattedMessage from 'I18n/components/FormattedMessage';
import getUsageTracker from 'pricing-pages-lib/utils/getUsageTracker';
import { getProductConfigurationsWithoutProductLine, getUpdatedProductConfigurationsWithSkuId } from 'pricing-pages-lib/utils/productConfigUtils';
import { getCurrentSkuIdForProduct } from 'pricing-pages-lib/utils/productsUtils';
import { useCallback, useContext, useId } from 'react';
import { shallowEqual } from 'react-redux';
import { currentOrderedPaidProductLevels } from 'self-service-api/constants/ProductLevels';
import { MARKETING } from 'self-service-api/constants/ProductLines';
import { CONTACT, USER } from 'self-service-api/constants/UnitTypes';
import styled from 'styled-components';
import UICardHeader from 'UIComponents/card/UICardHeader';
import UICardSection from 'UIComponents/card/UICardSection';
import UICardWrapper from 'UIComponents/card/UICardWrapper';
import AccordionTransition from 'UIComponents/transitions/AccordionTransition';
import { PlanBuilderContext } from '../../../context/PlanBuilderContext';
import { useAppSelector } from '../../../reducers';
import { activeCurrencySelector } from '../../../selectors/CurrencySelectors';
import { bundledProductLinesForSkuIdSelector } from '../../../selectors/ProductConfigsSelectors';
import { productVersionsFromProductInventorySelector } from '../../../selectors/ProductInventorySelectors';
import { currentProductVersionSelector, currentSkuIdForProductSelector, productForSkuIdSelector, quantityPackForProductVersionSelector, skuIdHasBundledProductsSelector, skuIdIsQuantityPackSelector, skuSelector } from '../../../selectors/ProductsSelectors';
import { gatesSelector, productDataSelector, productInventorySelector } from '../../../selectors/SelectorUtils';
import { productNamesByProductLevelMapSelector } from '../../../selectors/TrackingSelectors';
import { seleniumUnitQuantitiesStringSelector, seleniumUnitsTypesStringSelector } from '../../../utils/planBuilderUtils/SeleniumUtils';
import { SectionTitle } from '../../common/SharedComponents';
import QuantityPacksPickerContainer from '../QuantityPacksPickerContainer';
import HubProductLevelPicker from './HubProductLevelPicker';
import PlanPickerHeader from './PlanPickerHeader';
const StyledPlanPickerWrapper = styled.div.withConfig({
  displayName: "HubPlanPickerList__StyledPlanPickerWrapper",
  componentId: "sc-1srbl92-0"
})(["display:flex;flex-direction:column;flex:1;width:inherit;"]);
const Container = styled(UICardWrapper).withConfig({
  displayName: "HubPlanPickerList__Container",
  componentId: "sc-1srbl92-1"
})(["max-width:700px;"]);
const StyledCardHeader = styled(UICardHeader).withConfig({
  displayName: "HubPlanPickerList__StyledCardHeader",
  componentId: "sc-1srbl92-2"
})(["& > div > span{padding-bottom:0;}"]);

const PlanPicker = ({
  productLine,
  className,
  compact
}) => {
  const {
    productConfigurations,
    setProductConfigurations,
    requiredProductLines,
    trackingSource,
    screen
  } = useContext(PlanBuilderContext);
  const currencyCode = useAppSelector(activeCurrencySelector);
  const required = requiredProductLines.includes(productLine); // do you own something from `productLine`

  const ownedSkuId = useAppSelector(state => {
    const ownedProductVersion = productVersionsFromProductInventorySelector(state).find(({
      skuId
    }) => {
      if (skuIdHasBundledProductsSelector(state, {
        skuId
      })) {
        const bundledProducts = bundledProductLinesForSkuIdSelector(state, {
          skuId
        });
        return bundledProducts.includes(productLine);
      }

      const product = productForSkuIdSelector(state, {
        skuId
      });
      return product.productLine === productLine && product.productLevel;
    });
    return ownedProductVersion && ownedProductVersion.skuId;
  }); // if you own something from `productLine`, is it the current, latest version?

  const ownedCurrentSkuId = useAppSelector(state => {
    if (!ownedSkuId) return null;
    const {
      apiName: ownedApiName
    } = productForSkuIdSelector(state, {
      skuId: ownedSkuId
    });
    const {
      skuId: currentProductVersionSkuId
    } = currentProductVersionSelector(state, {
      apiName: ownedApiName
    });
    return ownedSkuId === currentProductVersionSkuId ? ownedSkuId : null;
  }); //which sku for this productLine should be selected

  const selectedSkuId = useAppSelector(state => {
    const foundConfig = productConfigurations.find(({
      skuId
    }) => {
      const product = productForSkuIdSelector(state, {
        skuId
      });
      const hasBundleWithProductLine = skuIdHasBundledProductsSelector(state, {
        skuId
      }) && bundledProductLinesForSkuIdSelector(state, {
        skuId
      }).includes(productLine);
      return product.productLine === productLine && product.productLevel || hasBundleWithProductLine;
    });
    let updatedOwnedSkuId;

    if (ownedCurrentSkuId) {
      const {
        productId
      } = skuSelector(state, {
        skuId: ownedCurrentSkuId
      });
      updatedOwnedSkuId = ownedCurrentSkuId && currentSkuIdForProductSelector(state, {
        productId
      });
    }

    return foundConfig ? foundConfig.skuId : updatedOwnedSkuId;
  });
  const ownedProductLevel = useAppSelector(state => ownedCurrentSkuId ? productForSkuIdSelector(state, {
    skuId: ownedCurrentSkuId
  }).productLevel : null); // This can be different than ownedProductLevel if you own a legacy product (sidegrades)

  const disabledProductLevel = useAppSelector(state => {
    if (!ownedSkuId) return null;
    const {
      productLevel
    } = productForSkuIdSelector(state, {
      skuId: ownedSkuId
    });
    const currentOrderedPaidProductLevel = productLevel === currentOrderedPaidProductLevels[0] ? null : currentOrderedPaidProductLevels[currentOrderedPaidProductLevels.indexOf(productLevel) - 1];
    return currentOrderedPaidProductLevel;
  });
  const selectedProductLevel = useAppSelector(state => selectedSkuId ? productForSkuIdSelector(state, {
    skuId: selectedSkuId
  }).productLevel : null);
  const trackingProductNamesByProductLevelMap = useAppSelector(state => productNamesByProductLevelMapSelector(state, {
    productLine
  }), shallowEqual);
  const productData = useAppSelector(productDataSelector);
  const gates = useAppSelector(gatesSelector);
  const productInventory = useAppSelector(productInventorySelector);
  const setProductLevel = useCallback(newProductLevel => {
    if (!newProductLevel) {
      if (selectedProductLevel === ownedProductLevel) {
        return;
      } else {
        newProductLevel = ownedProductLevel;
      }
    }

    if (newProductLevel) {
      getUsageTracker().track('planBuilderInteraction', {
        component: 'HubPlanPickerList',
        action: 'clicked product level selection button',
        productName: trackingProductNamesByProductLevelMap[newProductLevel],
        skuId: getCurrentSkuIdForProduct({
          productData
        }, {
          productLine,
          productLevel: newProductLevel
        }),
        screen,
        currency: currencyCode,
        trackingSource
      });
    }

    setProductConfigurations(prevConfigs => {
      const filteredConfigs = getProductConfigurationsWithoutProductLine({
        productData,
        productInventory,
        gates
      }, {
        productConfigurations: prevConfigs,
        productLine
      });

      if (newProductLevel) {
        const newSkuId = getCurrentSkuIdForProduct({
          productData
        }, {
          productLine,
          productLevel: newProductLevel
        });

        if (newSkuId) {
          return getUpdatedProductConfigurationsWithSkuId({
            productData,
            productInventory,
            gates
          }, {
            productConfigurations: filteredConfigs,
            skuId: newSkuId
          });
        }
      }

      return filteredConfigs;
    });
  }, [productData, productLine, setProductConfigurations]);
  const additionalUnitSkuIds = useAppSelector(state => {
    let skuIds = [];

    if (selectedSkuId) {
      const skuIdIsQuantityPack = skuIdIsQuantityPackSelector(state, {
        skuId: selectedSkuId
      });

      if (skuIdIsQuantityPack) {
        skuIds = [selectedSkuId];
      }

      const quantityPack = quantityPackForProductVersionSelector(state, {
        productVersionSkuId: selectedSkuId,
        unitType: productLine === MARKETING ? CONTACT : USER,
        unitScope: productLine
      });

      if (quantityPack) {
        skuIds = [quantityPack.skuId];
      }
    }

    return skuIds;
  });
  const seleniumUnitsString = useAppSelector(state => seleniumUnitQuantitiesStringSelector(state, {
    productConfigurations,
    productVersionSkuId: selectedSkuId
  }));
  const seleniumUnitsTypesIdString = useAppSelector(state => seleniumUnitsTypesStringSelector(state, {
    productConfigurations,
    productVersionSkuId: selectedSkuId
  }));
  const apiName = useAppSelector(state => {
    const product = selectedSkuId && productForSkuIdSelector(state, {
      skuId: selectedSkuId
    });
    return product && product.apiName;
  });
  const hubProductLevelPickerLabelId = useId();
  const Wrapper = compact ? 'div' : Container;
  const SectionContainer = compact ? 'div' : UICardSection;

  const PlanPickerTitle = /*#__PURE__*/_jsx(PlanPickerHeader, {
    productLine: productLine,
    compact: compact // trackingSource={trackingSource}
    ,
    id: hubProductLevelPickerLabelId
  });

  return /*#__PURE__*/_jsxs(Wrapper, {
    className: className,
    "data-product-level": selectedProductLevel,
    "data-selenium-id": `${productLine}-plan-builder-picker`,
    "data-api-name": apiName,
    "data-units": seleniumUnitsString || '0',
    "data-units-type": seleniumUnitsTypesIdString,
    children: [compact ? /*#__PURE__*/_jsx("div", {
      children: PlanPickerTitle
    }) : /*#__PURE__*/_jsx(StyledCardHeader, {
      titleSize: "xs",
      title: PlanPickerTitle
    }), /*#__PURE__*/_jsxs(SectionContainer, {
      children: [/*#__PURE__*/_jsx(HubProductLevelPicker, {
        ariaLabelledById: hubProductLevelPickerLabelId,
        productLine: productLine,
        required: required,
        selectedProductLevel: selectedProductLevel || null,
        ownedProductLevel: ownedProductLevel,
        disabledProductLevel: disabledProductLevel,
        setSelectedProductLevel: setProductLevel
      }), /*#__PURE__*/_jsx(AccordionTransition, {
        open: additionalUnitSkuIds.length > 0,
        children: /*#__PURE__*/_jsx(QuantityPacksPickerContainer, {
          additionalUnitSkuIds: additionalUnitSkuIds,
          setProductLevel: setProductLevel,
          trackingSource: trackingSource
        })
      })]
    })]
  });
};

const HubPlanPickerList = ({
  productLines,
  compact = false
}) => {
  return /*#__PURE__*/_jsxs(StyledPlanPickerWrapper, {
    children: [!compact && /*#__PURE__*/_jsx(SectionTitle, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "planBuilder.selectProductsAndAddons"
      })
    }), productLines.map(productLine => /*#__PURE__*/_jsx(PlanPicker, {
      productLine: productLine,
      compact: compact
    }, productLine))]
  });
};

export default HubPlanPickerList;